import { ReportUploadPanel } from "@/components/report_upload_panel"

export const APEX_PORTAL_URL = "https://albertsons.apexportal.net/Reports/CashForecast"

/** What `POST /integration/albertsons/upload` answers with. */
export interface AlbertsonsUploadResponse {
  job_id: string
  /** Rows in the export that nothing has booked yet. */
  to_insert: number
  /** Rows the remittance emails or an earlier upload already booked, which are left alone. */
  already_booked: number
  /** Payments in the file that tie out. */
  payments: number
  /** Payments refused because the export did not carry all of their rows. */
  refused_payments: string[]
  first_payment_date: string | null
  last_payment_date: string | null
}

export function albertsons_upload_summary(response: AlbertsonsUploadResponse): {
  title: string
  description: string
} {
  const rows = `${response.to_insert} new row${response.to_insert === 1 ? "" : "s"}`
  const across = `${response.payments} payment${response.payments === 1 ? "" : "s"}`
  const skipped =
    response.already_booked > 0 ? ` ${response.already_booked} were already booked and were left alone.` : ""
  if (response.to_insert === 0) {
    return {
      title: "Upload received — nothing new to book",
      description: `Every row in this export is already booked (${response.already_booked} across ${across}), so nothing was duplicated.`,
    }
  }
  const booked = `${rows} from ${across} will be booked in the background.${skipped}`
  if (response.refused_payments.length === 0) {
    return { title: "Upload received", description: booked }
  }
  return {
    title: "Upload received — some payments were skipped",
    description: `${booked} Skipped because their rows do not add up to the payment amount: ${response.refused_payments.join(
      ", "
    )}. Export CashForecast again and re-upload; nothing is booked twice.`,
  }
}

interface AlbertsonsUploadSectionProps {
  /** Named by admin-style callers; a brand uploads to its own org and may omit it. */
  integrationId?: string
  onUploaded?: () => void
  className?: string
}

/**
 * Upload the Apex portal's `CashForecast` export. The remittance emails only exist from the day a
 * brand moved to EFT, so this is how the history before that — and any payment whose email never
 * arrived — reaches MarginWiz. The portal is behind two-factor sign-in, so the export is pulled by
 * hand.
 */
export function AlbertsonsUploadSection({ integrationId, onUploaded, className }: AlbertsonsUploadSectionProps) {
  return (
    <ReportUploadPanel<AlbertsonsUploadResponse>
      className={className}
      endpoint="/api/integration/albertsons/upload"
      fields={integrationId ? { integration_id: integrationId } : undefined}
      accept={{ "text/csv": [".csv"] }}
      dropLabel="Drag and drop the CashNowInvoiceOffer export"
      formatsLabel=".csv"
      successMessage={albertsons_upload_summary}
      onUploaded={() => onUploaded?.()}>
      <div class="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
        <p>Pull the payment history from the Albertsons Apex vendor portal:</p>
        <ol class="list-decimal list-outside space-y-1 ml-5">
          <li>
            Log in at{" "}
            <a
              href="https://albertsons.apexportal.net/"
              target="_blank"
              rel="noopener noreferrer"
              class="underline hover:text-blue-700">
              albertsons.apexportal.net
            </a>
            . Apex emails a confirmation code every time (two-factor), which is why we can't pull this for you.
          </li>
          <li>
            Open <strong>Reports</strong> and choose <strong>Cash Forecast</strong>.
          </li>
          <li>
            Set the date range to cover the history you want — <strong>this is the important part</strong>. Go back as
            far as the portal allows the first time; after that, back to the last payment you uploaded. Overlapping a
            range you already uploaded is fine, and so is overlapping the remittance emails — nothing is booked twice.
          </li>
          <li>
            Export the whole report, not a selection. It downloads as{" "}
            <code class="text-xs">CashNowInvoiceOffer.csv</code>.
          </li>
          <li>Upload that file here.</li>
        </ol>
        <p class="text-xs text-blue-800 italic">
          Every invoice Albertsons paid and every claim it netted out (allowances, pre-payments, compliance fines,
          scan-based chargebacks, Inmar coupons…) becomes a transaction on its payment. We only book payments whose
          rows add up to the payment amount, and we skip invoices Albertsons has not paid yet — those appear once a
          payment covers them.
        </p>
      </div>
    </ReportUploadPanel>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function response(partial: Partial<AlbertsonsUploadResponse>): AlbertsonsUploadResponse {
    return {
      job_id: "j",
      to_insert: 0,
      already_booked: 0,
      payments: 0,
      refused_payments: [],
      first_payment_date: null,
      last_payment_date: null,
      ...partial,
    }
  }

  describe("albertsons_upload_summary", () => {
    it("says how many rows will be booked, and how many were already there", () => {
      const summary = albertsons_upload_summary(response({ to_insert: 2269, already_booked: 77, payments: 554 }))
      expect(summary.title).toBe("Upload received")
      expect(summary.description).toContain("2269 new rows from 554 payments")
      expect(summary.description).toContain("77 were already booked")
    })

    it("says so plainly when a re-upload adds nothing", () => {
      const summary = albertsons_upload_summary(response({ to_insert: 0, already_booked: 77, payments: 11 }))
      expect(summary.title).toContain("nothing new")
      expect(summary.description).toContain("nothing was duplicated")
    })

    it("names the payments the export cut short", () => {
      const summary = albertsons_upload_summary(
        response({ to_insert: 5, payments: 3, refused_payments: ["8101234567"] })
      )
      expect(summary.title).toContain("skipped")
      expect(summary.description).toContain("8101234567")
      expect(summary.description).toContain("nothing is booked twice")
    })

    it("counts one row and one payment in the singular", () => {
      const summary = albertsons_upload_summary(response({ to_insert: 1, payments: 1 }))
      expect(summary.description).toContain("1 new row from 1 payment ")
    })
  })
}
